import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  Fragment,
  useRef,
} from "react";
import {
  Button,
  Tooltip,
  PlainButton,
  MobileDrawer,
  LoadingSpinner,
  customerPhoneStringToInputValue,
  AvatarSelect,
  SidebarDrawer,
} from "@jugl-web/ui-kit";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useForm, FieldValues, FieldError } from "react-hook-form";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { useTranslations, useAppVariant } from "@jugl-web/utils";
import { Customer } from "@jugl-web/rest-api/customer";
import { processCustomerFormsToApi } from "@jugl-web/domain-resources/customers/utils/processCustomerFormsToApi";
import { DiscardChangesConfirmationAlert } from "@jugl-web/domain-resources/tasks/components/DiscardChangesConfirmationAlert";
import { CustomerFormField } from "../CustomerFormField/CustomerFormField";
import { sortFormFields } from "../../utils/sortFormFields";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as InfoIcon } from "./assets/info.svg";
import { ReactComponent as AvatarLogo } from "./assets/avatar-logo.svg";
import { ReactComponent as PersonInfoIcon } from "./assets/person.svg";
import { ReactComponent as CompanyInfoIcon } from "./assets/company.svg";
import { ReactComponent as ArrowLeftIcon } from "./assets/arrow-left.svg";
import { CustomizeFieldsDialog } from "../CustomizeFieldsDialog/CustomizeFieldsDialog";
import { useCustomerFields } from "../../hooks/useCustomerFields";

export const ManageCustomerDialog: React.FC<{
  entityId: string;
  isOpen: boolean;
  customerId?: string;
  initialParams?: { [key: string]: string };
  isCloseBtnHidden?: boolean;
  onGoBack?: () => void;
  onClose: () => void;
  onSuccess?: (data: Customer, isEdit?: boolean) => void;
}> = ({
  entityId,
  isOpen,
  customerId,
  initialParams,
  isCloseBtnHidden,
  onGoBack,
  onClose,
  onSuccess,
}) => {
  const { isMobile } = useAppVariant();
  const { customersApi } = useRestApiProvider();
  const { t } = useTranslations();
  const { data: customer, isLoading } = customersApi.useCustomerQuery(
    entityId && customerId
      ? { entityId, customerId, meta: { silent: true } }
      : skipToken
  );
  const [addNewCustomer] = customersApi.useAddCustomerMutation();
  const [updateCustomer] = customersApi.useUpdateCustomerMutation();
  const [inProgress, setInProgress] = useState(false);
  const submittingRef = useRef(false);
  const [logoFile, setLogoFile] = useState<File>();
  const [isCustomizeFieldsOpen, setCustomizeFieldsOpen] = useState(false);
  const [isDiscardAlertOpen, setIsDiscardAlertOpen] = useState(false);
  const { editableFormFields } = useCustomerFields({ entityId });
  const {
    reset,
    control,
    watch,
    handleSubmit,
    register,
    formState: { errors, dirtyFields, submitCount, isDirty },
  } = useForm({
    defaultValues: initialParams || {},
    mode: "onChange",
  });
  const formParams = watch();

  useEffect(() => {
    if (customerId) {
      const values: { [key: string]: string } = {};
      customer?.data.fields?.forEach((item) => {
        if (item.value) {
          values[item.field.id] = item.value;
        }
      });
      reset(values);
    } else {
      reset(initialParams || {});
    }
  }, [customer, reset, isOpen, customerId, initialParams]);

  useEffect(() => {
    if (!isOpen) {
      reset({});
      setInProgress(false);
      submittingRef.current = false;
      setLogoFile(undefined);
    }
  }, [isOpen, reset]);

  const onSubmit = useCallback(
    async (data: FieldValues) => {
      if (submittingRef.current || !entityId || !editableFormFields) return;

      submittingRef.current = true;
      setInProgress(true);

      const newCustomer = processCustomerFormsToApi(editableFormFields, data);
      const response = await addNewCustomer({
        entityId,
        fields: { fields: newCustomer },
        logoFile,
        meta: {
          successMessage: t({
            id: "feedback.customer-added",
            defaultMessage: "Customer has been added",
          }),
        },
      });

      if (response && "data" in response) {
        onClose();
        reset({});
        onSuccess?.(response.data.data);
      } else {
        submittingRef.current = false;
        setInProgress(false);
      }
    },
    [
      addNewCustomer,
      editableFormFields,
      entityId,
      logoFile,
      onClose,
      onSuccess,
      reset,
      t,
    ]
  );

  const onUpdate = useCallback(
    async (data: FieldValues) => {
      if (submittingRef.current) return;

      const dirtyData = Object.fromEntries(
        Object.entries(data).filter(([key]) => dirtyFields[key])
      );

      const hasAnyDirtyField = Object.keys(dirtyData).length > 0;
      const hasLogoChanged = !!logoFile;

      if (!hasAnyDirtyField && !hasLogoChanged) {
        onClose();
        return;
      }

      if (!entityId || !customerId || !editableFormFields) return;

      submittingRef.current = true;
      setInProgress(true);

      const fieldsData = processCustomerFormsToApi(
        editableFormFields,
        dirtyData
      );
      const response = await updateCustomer({
        customerId,
        entityId,
        fields: { fields: fieldsData },
        logoFile,
        meta: {
          successMessage: t({
            id: "feedback.customer-updated",
            defaultMessage: "Customer has been updated",
          }),
        },
      });

      if (response && "data" in response) {
        onSuccess?.(response.data.data, true);
        onClose();
        reset({});
      } else {
        submittingRef.current = false;
        setInProgress(false);
      }
    },
    [
      customerId,
      dirtyFields,
      editableFormFields,
      entityId,
      logoFile,
      onClose,
      onSuccess,
      reset,
      t,
      updateCustomer,
    ]
  );

  const handleSave = handleSubmit(customerId ? onUpdate : onSubmit);

  const isSameCustomer = customerId && customer?.data?.id === customerId;

  const onLogoSave = (value?: File) => {
    if (value) {
      setLogoFile(value);
    }
  };

  const isFieldInvalid = useCallback(
    (fieldId: string) => fieldId in errors,
    [errors]
  );

  const mobilePersonalField = useMemo(
    () =>
      editableFormFields.find(
        (field) =>
          field.name === "phone_number" &&
          field.is_cust_field === false &&
          field.section === "personal"
      ),
    [editableFormFields]
  );

  const emailPersonalField = useMemo(
    () =>
      editableFormFields.find(
        (field) =>
          field.name === "email" &&
          field.is_cust_field === false &&
          field.section === "personal"
      ),
    [editableFormFields]
  );

  const mobilePersonalValue = useMemo(() => {
    const inputsValue =
      mobilePersonalField?.id && formParams[mobilePersonalField.id];
    if (!inputsValue) {
      return undefined;
    }
    return customerPhoneStringToInputValue(inputsValue).phone;
  }, [formParams, mobilePersonalField?.id]);

  const managedFields = useMemo(() => {
    const emailPersonalValue =
      emailPersonalField?.id && formParams[emailPersonalField.id];

    const sortedFields = sortFormFields(editableFormFields || []);

    const fields = sortedFields.map((field) => {
      if (field.id === mobilePersonalField?.id) {
        return {
          ...field,
          required: !emailPersonalValue,
        };
      }
      if (field.id === emailPersonalField?.id) {
        return {
          ...field,
          required: !mobilePersonalValue,
        };
      }
      return field;
    });

    return fields;
  }, [
    emailPersonalField?.id,
    formParams,
    editableFormFields,
    mobilePersonalField?.id,
    mobilePersonalValue,
  ]);

  const shouldShowAddPhoneOrMail = useMemo(() => {
    const emailPersonalValue =
      emailPersonalField?.id && formParams[emailPersonalField.id];
    if (submitCount && !emailPersonalValue && !mobilePersonalValue) {
      return true;
    }
    return false;
  }, [emailPersonalField?.id, formParams, mobilePersonalValue, submitCount]);

  const content = useMemo(() => {
    return (
      <>
        <div className="flex flex-col items-center py-6">
          <AvatarSelect
            value={logoFile}
            onSave={onLogoSave}
            defaultSrc={
              (customerId && isSameCustomer && customer?.data?.logo) ||
              undefined
            }
            placeholder={<AvatarLogo />}
          />
        </div>
        <div className="px-8 py-6">
          <div className="flex items-center gap-2 border-0 border-b-[1px] border-solid border-grey-200 py-2 font-medium text-[#383838]">
            <PersonInfoIcon />
            {t({
              id: "customers-page.personal-info-1",
              defaultMessage: "Personal Info",
            })}
          </div>
          <div className="flex flex-col gap-6 pt-6">
            {managedFields?.map((field) => (
              <Fragment key={field.id}>
                <CustomerFormField
                  field={field}
                  section="personal"
                  register={register}
                  control={control}
                  value={formParams[field.id]}
                  invalid={isFieldInvalid(field.id)}
                  error={errors[field.id] as FieldError}
                  inEditMode={!!customerId}
                />
                {shouldShowAddPhoneOrMail &&
                  (field.id === mobilePersonalField?.id ||
                    field.id === emailPersonalField?.id) && (
                    <span
                      className="text-xs text-gradients-danger"
                      key={`error-1-${field.id}`}
                    >
                      {t({
                        id: "tasks-details-page.add-phone-or-mail-to-proceed",
                        defaultMessage: "Add phone number or email to proceed",
                      })}
                    </span>
                  )}
                {field.id === mobilePersonalField?.id &&
                  isFieldInvalid(field.id) && (
                    <span
                      className="text-xs text-gradients-danger"
                      key={`error-2-${field.id}`}
                    >
                      {t({
                        id: "feedback.invalid-phone-number",
                        defaultMessage: "Invalid phone number",
                      })}
                    </span>
                  )}
              </Fragment>
            ))}
          </div>
          <div className="flex items-center gap-2 border-0 border-b-[1px] border-solid border-grey-200 py-2 pt-12 font-medium text-[#383838]">
            <CompanyInfoIcon />
            {t({
              id: "customers-page.company-info",
              defaultMessage: "Company Info",
            })}
          </div>
          <div className="flex flex-col gap-6 pt-6">
            {managedFields?.map((field) => (
              <CustomerFormField
                key={field.id}
                field={field}
                section="company"
                register={register}
                control={control}
                value={formParams[field.id]}
                invalid={isFieldInvalid(field.id)}
                error={errors[field.id] as FieldError}
                inEditMode={!!customerId}
              />
            ))}
          </div>
        </div>
      </>
    );
  }, [
    control,
    customer?.data?.logo,
    customerId,
    emailPersonalField?.id,
    errors,
    formParams,
    isFieldInvalid,
    isSameCustomer,
    logoFile,
    managedFields,
    mobilePersonalField?.id,
    register,
    shouldShowAddPhoneOrMail,
    t,
  ]);

  if (isMobile) {
    return (
      <MobileDrawer
        isOpen={isOpen}
        header={{
          title: (
            <div className="flex items-center justify-center gap-4">
              {onGoBack && (
                <PlainButton
                  onClick={onGoBack}
                  className="flex h-[32px] w-[32px] items-center justify-center"
                >
                  <ArrowLeftIcon />
                </PlainButton>
              )}
              {customerId
                ? t({
                    id: "customers-page.edit-customer",
                    defaultMessage: "Edit Customer",
                  })
                : t({
                    id: "customers-page.add-new-customer",
                    defaultMessage: "Add New Customer",
                  })}
            </div>
          ),
          hasCloseButton: !isCloseBtnHidden,
        }}
        onClose={() => {
          onClose();
          reset({});
        }}
      >
        <MobileDrawer.Content className="px-0">{content}</MobileDrawer.Content>
        <MobileDrawer.Actions className="flex h-[80px] items-center justify-center border-x-0 border-t-[2px] border-b-0 border-solid border-[#EEF2F5]">
          <Button
            className="h-10 w-[300px]"
            onClick={handleSave}
            isDisabled={inProgress}
          >
            {t({
              id: "common.save",
              defaultMessage: "Save",
            })}
          </Button>
        </MobileDrawer.Actions>
      </MobileDrawer>
    );
  }

  return (
    <>
      <SidebarDrawer
        isOpen={isOpen}
        onClose={() => {
          if (
            isDirty ||
            (initialParams && Object.keys(initialParams).length > 0)
          ) {
            setIsDiscardAlertOpen(true);
            return;
          }
          onClose();
          reset({});
        }}
        customHeader={
          <div className="flex h-[63px] w-full shrink-0 items-center justify-between gap-2 border-0 border-b-[1px] border-solid border-dark-100 px-8">
            <span className="flex items-center gap-4 font-secondary text-[15px] leading-2 font-semibold text-[#333333] drop-shadow-[-2px_0_3px_rgba(0,0,0,0.05)]">
              {onGoBack && (
                <PlainButton
                  onClick={onGoBack}
                  className="flex h-[32px] w-[32px] items-center justify-center"
                >
                  <ArrowLeftIcon />
                </PlainButton>
              )}
              {customerId
                ? t({
                    id: "customers-page.edit-customer",
                    defaultMessage: "Edit Customer",
                  })
                : t({
                    id: "customers-page.add-new-customer",
                    defaultMessage: "Add New Customer",
                  })}
            </span>
            <div className="flex gap-4">
              <Tooltip
                placement="bottom"
                className="text-center font-secondary"
                renderTrigger={({ props, ref }) => (
                  <PlainButton
                    {...props}
                    ref={ref}
                    className="rounded-lg p-1 transition-all hover:bg-grey-100"
                    aria-label={t({
                      id: "customers-page.sidebar-tooltip-info",
                      defaultMessage: "Information",
                    })}
                  >
                    <InfoIcon />
                  </PlainButton>
                )}
              >
                {t({
                  id: "customers-page.sidebar-tooltip-info",
                  defaultMessage:
                    "Information about Customers can be used while forming Invoices. Please make sure to enter valid data about Customer 🤓",
                })}
              </Tooltip>
              {!isCloseBtnHidden && (
                <PlainButton onClick={onClose}>
                  <CloseIcon />
                </PlainButton>
              )}
            </div>
          </div>
        }
      >
        <SidebarDrawer.Content className="relative font-secondary">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : (
            content
          )}
        </SidebarDrawer.Content>
        {!isLoading && (
          <SidebarDrawer.Actions className="flex w-full items-center justify-center gap-4 border-x-0 border-t-[2px] border-b-0 border-solid border-[#EEF2F5] py-6">
            <Button
              type="submit"
              className="h-10 w-[300px]"
              onClick={handleSave}
              isDisabled={inProgress}
            >
              {t({
                id: "common.save",
                defaultMessage: "Save",
              })}
            </Button>
          </SidebarDrawer.Actions>
        )}
        <CustomizeFieldsDialog
          entityId={entityId}
          isOpen={isCustomizeFieldsOpen}
          onClose={() => {
            onClose();
            reset({});
            setCustomizeFieldsOpen(false);
          }}
        />
      </SidebarDrawer>
      <DiscardChangesConfirmationAlert
        isOpen={isDiscardAlertOpen}
        onClose={() => setIsDiscardAlertOpen(false)}
        onConfirm={() => {
          onClose();
          setIsDiscardAlertOpen(false);
          reset({});
        }}
      />
    </>
  );
};
